import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

interface HoverTooltipProps {
    content: string;
    className?: string;
    children: React.ReactNode;
}

type TooltipPosition = {
    top: number;
    left: number;
};

const TOOLTIP_OFFSET = 8;
const VIEWPORT_PADDING = 8;

const HoverTooltip = ({content, className = '', children}: HoverTooltipProps) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLSpanElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [position, setPosition] = useState<TooltipPosition>({top: 0, left: 0});

    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) {
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        left = Math.max(VIEWPORT_PADDING, Math.min(left, viewportWidth - tooltipRect.width - VIEWPORT_PADDING));

        let top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET;

        if (top < VIEWPORT_PADDING) {
            top = Math.min(triggerRect.bottom + TOOLTIP_OFFSET, viewportHeight - tooltipRect.height - VIEWPORT_PADDING);
        }

        setPosition({top, left});
        setIsPositioned(true);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsPositioned(false);
        updatePosition();

        const handleViewportChange = () => updatePosition();

        window.addEventListener('scroll', handleViewportChange, true);
        window.addEventListener('resize', handleViewportChange);

        return () => {
            window.removeEventListener('scroll', handleViewportChange, true);
            window.removeEventListener('resize', handleViewportChange);
        };
    }, [isOpen, updatePosition]);

    return (
        <div
            ref={triggerRef}
            className={`relative inline-flex ${className}`}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
        >
            {children}
            {isOpen && typeof document !== 'undefined' && createPortal(
                <span
                    ref={tooltipRef}
                    className="pointer-events-none fixed z-[999] max-w-[min(18rem,calc(100vw-16px))] whitespace-normal rounded-lg border border-white/[0.08] bg-[#111111] px-2.5 py-1.5 text-[11px] text-white/85 shadow-lg"
                    style={{
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        visibility: isPositioned ? 'visible' : 'hidden',
                    }}
                >
                    {content}
                </span>,
                document.body
            )}
        </div>
    );
};

export default HoverTooltip;
